import { NodeProcessor } from "./types";

import {
    HeadingLevel,
    Paragraph,
    TextRun,
    AlignmentType,
    IParagraphOptions,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    ITableBordersOptions,
    ExternalHyperlink,
    TableLayoutType,
    ImageRun,
} from "docx-dotx-hf-fix";
import { Element } from "slate";
import { CustomElement } from "../../../../slate";
import { ParagraphElementType } from "../slate/BlockElements";
import { NumberingConfig } from "./numbering";
import { TableCellElementType, TableElementType, TableRowElementType } from "../plugins/Tables";
import { LinkElementType } from "../plugins/Links";
import { ImageElementType } from "../plugins/InlineImages";
import { ColumnsBlockElementType } from "../plugins/Columns/ColumnsBlockElement";
import { LastNumberingIDKey } from "./processingContext";



const HeadingLevels = [
    HeadingLevel.TITLE, // won't hit because we don't have h0
    HeadingLevel.HEADING_1,
    HeadingLevel.HEADING_2,
    HeadingLevel.HEADING_3,
    HeadingLevel.HEADING_4,
    HeadingLevel.HEADING_5,
    HeadingLevel.HEADING_6,
]



const alignments: Record<any, AlignmentType> = {
    "left": AlignmentType.LEFT,
    "right": AlignmentType.RIGHT,
    "center": AlignmentType.CENTER,
    "justify": AlignmentType.JUSTIFIED,
}

export const commonBlockProperties = (n: CustomElement): Partial<IParagraphOptions> => ({
    alignment: n.align ? (alignments[n.align] || undefined) : undefined,
});

const getTableColumnsEvenWidths = (table: any): number[] | undefined => {
    const rows = table.children as any[] || [];
    if(!rows || !rows.length) {
        return undefined;
    }
    
    if(table.columns) {
      const widths = (table.columns as string).replace(",", " ").split(/\s+/).map(s => s.trim()).map(s => +s);
      if(widths.length && widths.every(w => !isNaN(w))) {
        return widths.map(x => 1.0 * x);
      }
    }

    const columns = rows[0].children;
    if(!columns || !columns.length) {
        return undefined;
    }
    const nColumns = columns.length;
    return columns.map(() => 100 / nColumns);
}

export const NoBorders: ITableBordersOptions = {
    top: { style: BorderStyle.NONE },
    bottom: { style: BorderStyle.NONE },
    left: { style: BorderStyle.NONE },
    right: { style: BorderStyle.NONE },
    insideHorizontal: { style: BorderStyle.NONE },
    insideVertical:{ style: BorderStyle.NONE },
}




export const NodeProcessors: NodeProcessor[] = [
    {
        shallProcess: n => (n as any).type === ParagraphElementType,
        process: (n, g, s, ctx) => new Paragraph({
            children: g(n.children || [], s, ctx),
            ...commonBlockProperties(n),
            indent: (n as any).no_indent || s?.no_indent ? { start: 0, firstLine: 0 } : undefined,
        }),
    },


    // LISTS

    {
        shallProcess: n => (n as any).type === "ul",
        process: (n, g, s, ctx) => g(n.children || [], { ul: 0 }, ctx),
    },
    {
        shallProcess: n => (n as any).type === "ol",
        process: (n, g, s, ctx) => {
          const lastNumberingInstanceId = ctx.data[LastNumberingIDKey];
          const globalListNumbering = !(n as any).force_restart_numbering ? ctx.data["__global_list_numbering_id"] : null;
          const shouldKeepNumbering = (ctx.lastProcessedNode as any).type === "ol" && !!lastNumberingInstanceId && !(n as any).force_restart_numbering;

          const nextNumberingInstanceId = globalListNumbering
            ? +globalListNumbering
            : shouldKeepNumbering
              ? lastNumberingInstanceId
              : ctx.nextNumberingInstance();
          return g(n.children || [], { ol: 0, numberingInstance: nextNumberingInstanceId }, ctx);
        },
    },
    {
        shallProcess: n => (n as any).type === "li",
        process: (n, g, settings, ctx) => {
          const childrenText = (n.children || []).map((c: any) => c.text as string || "").join("");
          if(!childrenText) {
            return [];
          }
          
          return new Paragraph({
            children: g(n.children || [], settings, ctx),
            ...commonBlockProperties(n),
            ...(settings?.ul !== undefined ? { bullet: { level: settings.ul }} : {}),
            ...(settings?.ol !== undefined ? { numbering: { reference: NumberingConfig.reference, level: settings.ol, instance: settings.numberingInstance }} : {}),
        })
        },
    },


    {
      shallProcess: n => (n as any).type === ColumnsBlockElementType,
      process: (n, g, s, ctx) => new Table({
          rows: [
            new TableRow({
              children: (n.columns_content || []).map((columnContent: any) => new TableCell({
                children: g(
                  columnContent?.blocks || [],
                  { ...(s || {}), no_indent: true },
                  ctx),
            }))
            })
          ],
          borders: NoBorders,
          layout: TableLayoutType.FIXED,
          columnWidths: Array.from({ length: n.columns_content.length || 1 }).map(x => 100/(n.columns_content.length || 1)),
          width: {
              size: 100,
              type: WidthType.PERCENTAGE,
          },
      }),
    },

    // TABLES

    {
        shallProcess: n => (n as any).type === TableElementType,
        process: (n, g, s, ctx) => new Table({
            rows: g(
              n.children || [],
              { ...(s || {}), no_indent: (n as any).no_indent ? true : false },
              ctx),
            borders: (n as any).no_border ? NoBorders : undefined,
            layout: (n as any).even_columns ? TableLayoutType.FIXED : undefined,
            columnWidths: (n as any).even_columns ? getTableColumnsEvenWidths(n) : undefined,
            width: {
                size: 100,
                type: WidthType.PERCENTAGE,
            },
        }),
    },
    {
        shallProcess: n => (n as any).type === TableRowElementType,
        process: (n, g, s, ctx) => new TableRow({
            children: g(n.children || [], s, ctx),
        }),
    },
    {
        shallProcess: n => (n as any).type === TableCellElementType,
        process: (n, g, s, ctx) => new TableCell({
            children: [new Paragraph({
                children: g(n.children || [], s, ctx),
                ...commonBlockProperties(n),
                indent: (n as any).no_indent || s?.no_indent ? { start: 0, firstLine: 0 } : undefined,
            })],
        }),
    },


    {
        shallProcess: n => (n as any).type === LinkElementType,
        process: (n, g, s, ctx) => new ExternalHyperlink({
            children: g(n.children || [], s, ctx),
            link: n.url,
            ...commonBlockProperties(n),
        }),
    },
    {
        shallProcess: n => (n as any).type === ImageElementType,
        process: (n, g, s, ctx) => {
            const imageData = ctx && ctx.getImage(n.url);
            if(imageData) {
                return new ImageRun({
                    data: imageData as any,
                    transformation: {
                        width: (n.width || "50").replace(/[^0-9]/g, ""),
                        height: (n.height || "50").replace(/[^0-9]/g, ""),
                    },
                });
            } else {
                return g([{ text: "" }], s, ctx);
            }
        },
    },

    
    // TEXT / LEAFS

    {
        shallProcess: n => !Element.isElement(n),
        process: (n, g) => {
            const lines = (n.text as string || "").split("\n");
            if(lines.length === 1) {
                return new TextRun({
                    text: n.text,
                    bold: n.bold,
                    italics: n.italic,
                    underline: n.underline,
                    strike: n.strikethrough,
                    highlight: n.highlight ? "yellow" : undefined,
                });
            }
            return lines.map((l,idx) => new TextRun({
                text: l,
                break: idx !== 0 ? 1 : undefined,
                bold: n.bold,
                italics: n.italic,
                underline: n.underline,
                strike: n.strikethrough,
                highlight: n.highlight ? "yellow" : undefined,
            }));
        }
    },
    
    {
      // HEADINGS
        shallProcess: n => /h\d/.test((n as any).type || ""),
        process: (n, g, s, ctx) => new Paragraph({
            children: g(n.children || [], s, ctx),
            heading: HeadingLevels[+(n as any).type.substring(1)],
            ...commonBlockProperties(n),
            indent: (n as any).no_indent || s?.no_indent ? { start: 0, firstLine: 0 } : undefined,
        }),
    }
];